import { useMemo } from 'react';
import api from '../utils/api';
import { useQuery } from '@tanstack/react-query';
import useDebouncedValue from './useDebouncedValue';


export default function useUnitsSearch({ communityId, searchQuery }) {
  const { isLoading, error, data: units, refetch: refetchUnits } = useQuery({
    queryKey: ['community.units', communityId],
    queryFn: () => api.get(`/communities/${communityId}/units`).then(response => response.data),
  })

  // Con fraccionamientos de hasta ~10k viviendas, filtrar en cada tecla congela el cel.
  // Debounce: solo filtramos cuando el usuario deja de teclear un momento.
  const debouncedQuery = useDebouncedValue(searchQuery, 300)
  const normalizedQuery = debouncedQuery.trim().toLowerCase()
  const isSearching = normalizedQuery !== ""

  const filteredUnits = useMemo(() => {
    if (!units) return []
    if (!isSearching) return units

    // includes() sobre texto en minúsculas: O(n) y sin compilar un RegExp por item.
    // Además evita el bug anterior de usar el texto del usuario como patrón regex
    // (caracteres como "(" o "[" lanzaban "Invalid regular expression").
    return units.filter(el => {
      const nameMatches = el.name?.toLowerCase().includes(normalizedQuery)
      const ownersMatches = el.owners?.some(ow => ow.name?.toLowerCase().includes(normalizedQuery))
      return nameMatches || ownersMatches
    })
  }, [normalizedQuery, isSearching, units])

  const groupedStreetNames = useMemo(() => {
    if (!units) return []

    // Set para deduplicar en O(n) (antes era reduce + find = O(n²) sobre 10k items).
    const set = new Set<string>()
    units.forEach(el => { if (el.street_name) set.add(el.street_name) })
    return Array.from(set).sort()
  }, [units])

  return {
    units,
    isLoading,
    error,
    refetchUnits,
    filteredUnits,
    groupedStreetNames,
    isSearching,
  }
}
